// The world around the map: a gradient sky dome and an open sea that runs
// to the horizon, so the hex island sits in a place instead of floating in
// a dark void. Pattern borrowed from WorldForge's W1 presentation work (sky
// dome + horizon-matched fog).
//
// Both are unlit and ignore scene lighting; the sea takes the scene fog so
// it fades into the horizon haze, the dome does not (it *is* the haze).

import { useMemo } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import { SEA_COLOR, SKY_HORIZON, SKY_ZENITH } from './sceneRenderConfig.js';

const DOME_RADIUS = 450;
const SEA_RADIUS = 420;
/** Just under the deep-ocean water surface (-0.55 + 0.02) so tiles sit on top. */
const SEA_Y = -0.56;

export function SkyDome() {
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          uZenith: { value: new THREE.Color(SKY_ZENITH) },
          uHorizon: { value: new THREE.Color(SKY_HORIZON) },
        },
        vertexShader: /* glsl */ `
          varying vec3 vDir;
          void main() {
            vDir = normalize(position);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }`,
        fragmentShader: /* glsl */ `
          uniform vec3 uZenith;
          uniform vec3 uHorizon;
          varying vec3 vDir;
          void main() {
            // Haze band hugs the horizon, then eases into the zenith colour.
            float t = pow(clamp(vDir.y, 0.0, 1.0), 0.45);
            gl_FragColor = vec4(mix(uHorizon, uZenith, t), 1.0);
            #include <colorspace_fragment>
          }`,
        side: THREE.BackSide,
        depthWrite: false,
        fog: false,
      }),
    [],
  );
  return (
    <mesh renderOrder={-1000} frustumCulled={false} material={material}>
      <sphereGeometry args={[DOME_RADIUS, 32, 16]} />
    </mesh>
  );
}

/** `color` overrides the sea (the landlocked preview paints the void fog-coloured). */
export function OpenSea({ color = SEA_COLOR }: { readonly color?: string }) {
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, SEA_Y, 0]}
      renderOrder={RenderLayer.Terrain - 1}
      frustumCulled={false}
    >
      <circleGeometry args={[SEA_RADIUS, 64]} />
      <meshBasicMaterial color={color} fog />
    </mesh>
  );
}
